import type { ToolUsage } from '@reach/shared-types/agent-components';
import { Link } from 'react-router-dom';
import { buildAgentToolPath } from '../../../app-routes';
import type {
  HttpToolDefinition,
  ListedToolDefinition,
  SchemaLookupToolDefinition,
} from '../../../lib/api';
import { TEMPLATE_KIND_NAMES, TEMPLATE_KIND_ORDER, TOOL_LIST_COPY } from '../agents-copy';
import styles from './ToolListPage.module.css';

type ListedToolSummary = Pick<
  ListedToolDefinition,
  'id' | 'display_name' | 'description' | 'status' | 'usage'
>;
type ListedHttpTool = ListedToolSummary &
  Pick<HttpToolDefinition, 'execution_kind' | 'http_method' | 'path_template'>;
type ListedLookupTool = ListedToolSummary & Pick<SchemaLookupToolDefinition, 'execution_kind'>;

interface ToolRowProps {
  tool: ListedHttpTool | ListedLookupTool;
}

interface UsedInTag {
  key: string;
  label: string;
}

const listUsedIn = (usage: ToolUsage): UsedInTag[] => {
  const componentsUseTool = usage.components.length > 0;
  if (componentsUseTool) {
    return usage.components.map(component => ({
      key: component.componentId,
      label: component.name,
    }));
  }
  return TEMPLATE_KIND_ORDER.filter(kind => usage.templateKinds.includes(kind)).map(kind => ({
    key: kind,
    label: TOOL_LIST_COPY.templateUse(TEMPLATE_KIND_NAMES[kind]),
  }));
};

const describePublishedVersion = (usage: ToolUsage): string | null => {
  const { publishedVersion, latestVersion } = usage;
  const toolIsPublished = publishedVersion !== null && latestVersion !== null;
  if (!toolIsPublished) {
    return null;
  }
  const publishedIsBehindLatest = publishedVersion < latestVersion;
  return publishedIsBehindLatest
    ? TOOL_LIST_COPY.publishedBehindLatest(publishedVersion, latestVersion)
    : null;
};

export const ToolRow = ({ tool }: ToolRowProps) => {
  const { id, display_name, description, status, usage } = tool;
  const toolCallsPartnerApi = tool.execution_kind === 'http';
  const request = toolCallsPartnerApi ? (
    <code>
      <strong>{tool.http_method}</strong> {tool.path_template}
    </code>
  ) : (
    TOOL_LIST_COPY.lookupRequest
  );
  const usedInTags = listUsedIn(usage);
  const toolIsUsed = usedInTags.length > 0;
  const usedIn = toolIsUsed ? (
    <ul>
      {usedInTags.map(tag => (
        <li key={tag.key}>{tag.label}</li>
      ))}
    </ul>
  ) : (
    TOOL_LIST_COPY.notUsed
  );
  const publishedVersionNote = describePublishedVersion(usage);

  return (
    <tr className={styles.toolRow}>
      <th scope="row">
        <Link to={buildAgentToolPath(id)}>{display_name}</Link>
        <p>{description}</p>
        {publishedVersionNote && <p>{publishedVersionNote}</p>}
      </th>
      <td data-execution-kind={tool.execution_kind}>{request}</td>
      <td data-tool-is-used={toolIsUsed}>{usedIn}</td>
      <td>
        <span data-status={status}>{TOOL_LIST_COPY.status[status]}</span>
      </td>
    </tr>
  );
};
